package com.nonopn.geolocation

import dev.jordond.compass.Priority
import dev.jordond.compass.geolocation.Geolocator
import dev.jordond.compass.geolocation.GeolocatorResult
import dev.jordond.compass.geolocation.LocationRequest
import dev.jordond.compass.geolocation.TrackingStatus
import dev.jordond.compass.geolocation.isPermissionDeniedForever
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.map

internal expect fun createLocator(): Geolocator

class GpsLocation {
    private val locator = try {
        createLocator()
    } catch (_: Throwable) {
        null
    }

    fun stopTracking() {
        locator?.stopTracking()
    }

    suspend fun startTracking(): com.nonopn.geolocation.TrackingStatus {
        val loc = locator?.startTracking(
            LocationRequest(
                priority = Priority.HighAccuracy
            )
        )
        return when (loc) {
            is TrackingStatus.Error -> com.nonopn.geolocation.TrackingStatus.Error(
                loc.cause.isPermissionDeniedForever()
            )

            TrackingStatus.Idle -> com.nonopn.geolocation.TrackingStatus.Idle
            TrackingStatus.Tracking -> com.nonopn.geolocation.TrackingStatus.Tracking
            is TrackingStatus.Update -> loc.location.coordinates.let {
                com.nonopn.geolocation.TrackingStatus.Success(
                    Location(
                        coordinates = loc.location.coordinates.let {
                            LatLng(
                                it.latitude,
                                it.longitude
                            )
                        },
                        accuracy = loc.location.accuracy,
                        altitude = loc.location.altitude?.let { Altitude(it.meters, it.accuracy) },
                        timestampMillis = loc.location.timestampMillis,
                        azimuth = loc.location.azimuth?.let { Azimuth(it.degrees, it.accuracy) },
                        speed = loc.location.speed?.let { Speed(it.mps, it.accuracy) },
                    )
                )
            }

            null -> com.nonopn.geolocation.TrackingStatus.Idle
        }
    }

    fun updates() = locator?.trackingStatus?.filterIsInstance<TrackingStatus.Update>()?.map {
        it.location.coordinates.let { LatLng(it.latitude, it.longitude) }
    }

    suspend fun current(): GpsResult {
        val loc = locator?.current(
            priority = Priority.HighAccuracy
        ) ?: return GpsResult.NotSupported("Geolocator not available")

        return when (loc) {
            is GeolocatorResult.Success -> GpsResult.Success(
                Location(
                    coordinates = loc.data.coordinates.let { LatLng(it.latitude, it.longitude) },
                    accuracy = loc.data.accuracy,
                    altitude = loc.data.altitude?.let { Altitude(it.meters, it.accuracy) },
                    timestampMillis = loc.data.timestampMillis,
                    azimuth = loc.data.azimuth?.let { Azimuth(it.degrees, it.accuracy) },
                    speed = loc.data.speed?.let { Speed(it.mps, it.accuracy) },
                )
            )

            is GeolocatorResult.Error -> when (loc) {
                is GeolocatorResult.NotSupported -> GpsResult.NotSupported(loc.message)
                is GeolocatorResult.NotFound -> GpsResult.NotFound(loc.message)
                is GeolocatorResult.GeolocationFailed -> GpsResult.GeolocationFailed(loc.message)
                is GeolocatorResult.PermissionDenied -> GpsResult.PermissionDenied(loc.isPermissionDeniedForever())
                else -> GpsResult.NotSupported("NotSupported")
            }
        }
    }
}

sealed class GpsResult {
    data class Success(val location: Location) : GpsResult()

    data class NotSupported(val message: String) : GpsResult()

    data class NotFound(val message: String) : GpsResult()

    data class GeolocationFailed(val message: String) : GpsResult()

    data class PermissionDenied(val isPermanentlyDenied: Boolean) : GpsResult()
}

sealed class TrackingStatus {
    data class Success(val location: Location) : com.nonopn.geolocation.TrackingStatus()

    data class Error(val isPermanentlyDenied: Boolean) : com.nonopn.geolocation.TrackingStatus()

    object Idle : com.nonopn.geolocation.TrackingStatus()

    object Tracking : com.nonopn.geolocation.TrackingStatus()
}
